/**
 * Copyright (c) 2017 CRF Box Oy. All Rights Reserved. Company Confidential.
 */

import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import {
  RESPONSES_NOT_SPECIFIED,
  RESPONSES_BIOSAMPLE_SAVING,
  RESPONSES_ADD_ITEM,
  RESPONSES_REMOVE_ITEM,
  RESPONSES_SAVE_ERROR,
  RESPONSES_UNIUE_ID_ERROR
} from '../../const';

import { UNIQUE_ID_ERROR_STATUS_CODE } from './constants';

const MANAGE_BIOSAMPLE_ITEM_ID = 'MANAGE_BIOSAMPLE_ITEM_ID';

const hasPermission =
  (permission) =>
  (permissions = []) =>
    permissions.includes(permission);

class BiosampleComponent extends React.Component {
  state = {
    value: '',
    error: null,
    loading: '',
    adding: this.props.biosamples.length === 0
  };

  componentDidUpdate() {
    const { biosamples } = this.props;
    const { adding } = this.state;

    if (biosamples.length === 0 && !adding) this.setAdding(true);
  }

  setValue = (value) => {
    this.setState(() => ({ value }));
  };

  setError = (error) => {
    this.setState(() => ({ error }));
  };

  setAdding = (adding) => {
    this.setState(() => ({ adding }));
  };

  setLoading = (loading) => {
    this.setState(() => ({ loading }));
  };

  updateValue = (event) => {
    const { biosamples } = this.props;

    this.setError(null);
    if (event.target.value === '' && biosamples.length !== 0) this.setAdding(false);
    this.setValue(event.target.value);
  };

  clearValue = () => {
    const { biosamples } = this.props;
    this.setValue('');
    this.setError(null);
    biosamples.length !== 0 && this.setAdding(false);
  };

  saveItemId = () => {
    const { value } = this.state;
    const { documentGid, questionId, documentType, addNewItemId, participantId, documentVersion } = this.props;
    const data = { documentGid, questionId, documentVersion, documentType, biosampleCode: value.trim() };

    this.setError(null);
    this.setLoading(true);

    addNewItemId(participantId, data)
      .then(() => {
        this.setValue('');
        this.setLoading(false);
        this.setAdding(false);
      })
      .catch((err) => {
        this.setLoading(false);
        if (err.response && err.response.status === UNIQUE_ID_ERROR_STATUS_CODE) {
          this.setError(err.response.data);
        } else {
          this.setError(RESPONSES_SAVE_ERROR);
        }
      });
  };

  render() {
    const { value, error, adding, loading } = this.state;
    const { permissions, editable, biosamples, showUpdateModal, showRemoveModal } = this.props;
    const hasValue = value.trim() !== '';
    const canEdit = (permission) => (permissions) => editable && hasPermission(permission)(permissions);

    if (loading) return RESPONSES_BIOSAMPLE_SAVING;
    if (adding && canEdit(MANAGE_BIOSAMPLE_ITEM_ID)(permissions))
      return (
        <span className="biosamples-column">
          <input
            type="text"
            maxLength="255"
            autoFocus={biosamples.length > 0}
            className={classnames({ saving: hasValue, error })}
            value={value}
            placeholder={RESPONSES_NOT_SPECIFIED}
            onChange={this.updateValue}
          />
          {hasValue && (
            <i onClick={this.saveItemId} className="material-icons biosample-code-save">
              save
            </i>
          )}
          {error && (
            <i title={error.message} className="material-icons biosample-code-add-error">
              error_outline
            </i>
          )}
          {(hasValue || biosamples.length !== 0) && (
            <i onClick={this.clearValue} className={classnames('biosample-code-clear-input', { saving: hasValue })}>
              &times;
            </i>
          )}
        </span>
      );

    return (
      <span className="biosamples-column">
        {biosamples.map(({ id, code }, index) => (
          <div key={index} className="biosample-code" title={code}>
            <div className="biosample-code-label">{code}</div>
            <div key={index} className="biosample-code-preview">
              <div
                className="biosample-code-label"
                onClick={canEdit(MANAGE_BIOSAMPLE_ITEM_ID)(permissions) ? showUpdateModal({ id, code }) : () => {}}
              >
                {code}
              </div>
              {canEdit(MANAGE_BIOSAMPLE_ITEM_ID)(permissions) && (
                <span
                  title={RESPONSES_REMOVE_ITEM}
                  className="biosample-code-remove"
                  onClick={showRemoveModal({ id, code })}
                />
              )}
            </div>
          </div>
        ))}
        {canEdit(MANAGE_BIOSAMPLE_ITEM_ID)(permissions) && (
          <i
            title={RESPONSES_ADD_ITEM}
            className="material-icons biosample-code-add"
            onClick={() => this.setAdding(true)}
          >
            add_circle_outline
          </i>
        )}
      </span>
    );
  }
}

BiosampleComponent.propTypes = {
  permissions: PropTypes.array,
  biosamples: PropTypes.array,
  showUpdateModal: PropTypes.func,
  showRemoveModal: PropTypes.func,
  documentGid: PropTypes.number,
  questionId: PropTypes.number,
  documentType: PropTypes.string,
  addNewItemId: PropTypes.func,
  participantId: PropTypes.number,
  documentVersion: PropTypes.string,
  editable: PropTypes.bool
};

// getBiosamplesComponent :: Object -> (() -> JSX)
const getBiosamplesComponent = (props) =>
  function Biosample() {
    return <BiosampleComponent {...props} />;
  };

export default getBiosamplesComponent;
